import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { ResidentState } from '@pflegenavi/shared/api';

export interface PhoenixResident {
  id: string;
  firstName: string;
  lastName: string;
  gender: string;
}

export interface GetResidentListPhoenixParams {
  nursingHomeId: string;
  tenant: string;
  residentIds?: string[];
  residentStates?: ResidentState[];
  page?: number;
  pageSize?: number;
}

export interface GetResidentListPhoenixResult {
  data: PhoenixResident[];
  meta: {
    total_count: number;
  };
}

export interface IServiceProviderAppPhoenixApi extends Api {
  getResidentList(dataIn: {
    params: GetResidentListPhoenixParams;
  }): Promise<PhoenixResident[]>;
}

export class ServiceProviderAppPhoenixApi
  implements IServiceProviderAppPhoenixApi
{
  baseUrl: string;
  headers: Headers;

  private _getResidentListRaw: (dataIn: {
    params: GetResidentListPhoenixParams;
  }) => Promise<GetResidentListPhoenixResult>;

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl('service-provider', apiUrl);
    this.headers = new Headers({
      'content-type': 'application/json',
    });

    this._getResidentListRaw = get({
      authContext,
      headers: this.headers,
      url: (params) => {
        const query = new URLSearchParams();

        // Required params
        query.append('tenant', params.tenant);

        // Pagination params
        if (params.page !== undefined) {
          query.append('page', (params.page + 1).toString()); // Convert 0-based to 1-based
        }
        if (params.pageSize !== undefined) {
          query.append('page_size', params.pageSize.toString());
        }

        // Build Flop filters
        let i = 0;

        // Filter by resident IDs
        if (params.residentIds && params.residentIds.length > 0) {
          query.append(`filters[${i}][field]`, 'id');
          query.append(`filters[${i}][op]`, 'in');
          for (const residentId of params.residentIds) {
            query.append(`filters[${i}][value][]`, residentId);
          }
          i++;
        }

        // Filter by resident states
        if (params.residentStates && params.residentStates.length > 0) {
          query.append(`filters[${i}][field]`, 'state');
          query.append(`filters[${i}][op]`, 'in');
          for (const state of params.residentStates) {
            query.append(`filters[${i}][value][]`, state);
          }
          i++;
        }

        return `${this.baseUrl}/nursing-home/${
          params.nursingHomeId
        }/residents?${query.toString()}`;
      },
    });
  }

  async getResidentList(dataIn: {
    params: GetResidentListPhoenixParams;
  }): Promise<PhoenixResident[]> {
    const result = await this._getResidentListRaw(dataIn);
    return result.data;
  }
}
